export default class HeightCalculator {
    // Static configuration
    static SELECTORS = {
        helloBar: '.hello-bar',
        header: '#header',
        searchBlockHeader: '.search-block .header',
        searchResult: '.search-result',
        navWrap: '.nav-wrap'
    };

    static CONSTANTS = {
        PADDING_OFFSET: 120,
        DEBOUNCE_DELAY: 150
    };

    constructor(options = {}) {
        // Allow custom configuration through options
        this.config = {
            ...HeightCalculator.CONSTANTS,
            ...options
        };

        // Initialize state
        this.elements = {};
        this.debouncedCalculateHeights = null;
        this.isInitialized = false;

        // Bind methods to preserve context
        this.calculateHeights = this.calculateHeights.bind(this);
        this.handleResize = this.handleResize.bind(this);
        this.destroy = this.destroy.bind(this);
    }

    /**
     * Initialize the height calculator
     * @returns {HeightCalculator} Instance for chaining
     */
    init() {
        if (this.isInitialized) {
            console.warn('HeightCalculator is already initialized');
            return this;
        }

        this.cacheElements();
        this.setupDebounce();
        this.bindEvents();
        this.calculateHeights();
        this.isInitialized = true;

        return this;
    }

    /**
     * Cache DOM elements for better performance
     * @private
     */
    cacheElements() {
        try {
            this.elements = {
                window,
                helloBar: document.querySelector(HeightCalculator.SELECTORS.helloBar),
                header: document.querySelector(HeightCalculator.SELECTORS.header),
                searchBlockHeader: document.querySelector(HeightCalculator.SELECTORS.searchBlockHeader),
                searchResults: document.querySelectorAll(HeightCalculator.SELECTORS.searchResult),
                navWraps: document.querySelectorAll(HeightCalculator.SELECTORS.navWrap)
            };

            if (!this.elements.header) {
                throw new Error('Required header element not found');
            }
        } catch (error) {
            console.error('Error caching elements:', error);
            throw error;
        }
    }

    /**
     * Setup debounce function for resize handling
     * @private
     */
    setupDebounce() {
        this.debouncedCalculateHeights = this.debounce(
            this.calculateHeights,
            this.config.DEBOUNCE_DELAY
        );
    }

    /**
     * Bind event listeners
     * @private
     */
    bindEvents() {
        ['resize', 'orientationchange'].forEach(event => {
            window.addEventListener(event, this.handleResize, { passive: true });
        });
    }

    /**
     * Handle resize events
     * @private
     */
    handleResize() {
        if (!this.isInitialized) return;
        this.debouncedCalculateHeights();
    }

    /**
     * Calculate and update heights
     * @public
     */
    calculateHeights() {
        if (!this.isInitialized) return;

        const windowHeight = this.elements.window.innerHeight;
        const helloBarHeight = this.elements.helloBar?.offsetHeight || 0;
        const headerHeight = this.elements.header?.offsetHeight || 0;
        const searchHeight = this.elements.searchBlockHeader?.offsetHeight || 0;

        const searchResultHeight = windowHeight - (
            helloBarHeight +
            headerHeight +
            searchHeight +
            this.config.PADDING_OFFSET
        );

        const navWrapHeight = windowHeight - (helloBarHeight + headerHeight);

        requestAnimationFrame(() => {
            this.updateElements(searchResultHeight, navWrapHeight);
        });
    }

    /**
     * Update DOM elements with calculated heights
     * @private
     * @param {number} searchResultHeight
     * @param {number} navWrapHeight
     */
    updateElements(searchResultHeight, navWrapHeight) {
        this.elements.searchResults?.forEach(block => {
            block.style.height = `${searchResultHeight}px`;
        });

        this.elements.navWraps?.forEach(block => {
            block.style.height = `${navWrapHeight}px`;
        });
    }

    /**
     * Utility function for debouncing
     * @private
     */
    debounce(func, wait) {
        let timeout;
        return (...args) => {
            clearTimeout(timeout);
            timeout = setTimeout(() => func.apply(this, args), wait);
        };
    }

    /**
     * Clean up and remove event listeners
     * @public
     */
    destroy() {
        if (!this.isInitialized) return;

        ['resize', 'orientationchange'].forEach(event => {
            window.removeEventListener(event, this.handleResize);
        });

        this.elements = {};
        this.isInitialized = false;
    }

    /**
     * Force recalculation of heights
     * @public
     */
    refresh() {
        if (!this.isInitialized) return;
        this.calculateHeights();
    }

    /**
     * Get current height calculations
     * @public
     * @returns {Object} Current height values
     */
    getHeights() {
        if (!this.isInitialized) return null;

        return {
            windowHeight: this.elements.window.innerHeight,
            helloBarHeight: this.elements.helloBar?.offsetHeight || 0,
            headerHeight: this.elements.header?.offsetHeight || 0,
            searchHeight: this.elements.searchBlockHeader?.offsetHeight || 0
        };
    }
}

/*
OLD CODE in Helpers
export const Height = () => {
  var windowHeight = $(window).height();
  var Height = function () {
    var HellobarHeight = $(".hello-bar").outerHeight() || 0,
      HeaderHeight = $("#header").outerHeight(),
      SearchHeight = $(".search-block .header").outerHeight() || 0;
    $(".search-result").each(function () {
      var block = $(this),
        itemHeight =
          windowHeight - (HellobarHeight + HeaderHeight + SearchHeight + 120);

      block.css("height", itemHeight);
    });

    $(".nav-wrap").each(function () {
      var block = $(this),
        itemHeight = windowHeight - (HellobarHeight + HeaderHeight);

      block.css("height", itemHeight);
    });
  };

  Height();
  $(window).on("resize orientationChange scroll", Height);
};

 */
